import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiCheckCircle,
  FiClock,
  FiHeadphones,
  FiMail,
  FiMapPin,
  FiPhoneCall,
  FiSend
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useHeroReveal, useScrollReveal } from '../hooks/useGsapMotion';

const CONTACT_HERO_IMAGE =
  'https://images.unsplash.com/photo-1556740714-a8395b3bf30f?auto=format&fit=crop&w=1400&q=80';
const CONTACT_HERO_FALLBACK =
  'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80';
const LOCATION_IMAGE =
  'https://images.unsplash.com/photo-1497366412874-3415097a27e7?auto=format&fit=crop&w=1400&q=80';
const LOCATION_IMAGE_FALLBACK =
  'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1400&q=80';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const pageRef = useRef(null);

  useHeroReveal(pageRef, []);
  useScrollReveal(pageRef, [formData.subject]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 450));

    toast.success('Message sent. Our team will reach out shortly.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    setSubmitting(false);
  };

  return (
    <div ref={pageRef} className="sc-container pb-16 pt-8 md:pt-10">
      <nav className="mb-7 flex items-center gap-2 text-sm text-slate-500" data-hero-item>
        <Link to="/" className="sc-hover-smooth hover:text-[#DB4444]">
          Home
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-700">Contact</span>
      </nav>

      <section className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12" data-hero-item>
        <aside className="space-y-6 lg:col-span-5">
          <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
            <img
              src={CONTACT_HERO_IMAGE}
              alt="Customer support"
              className="sc-hover-smooth h-64 w-full object-cover sm:h-72 group-hover:scale-[1.03]"
              onError={(event) => {
                event.currentTarget.src = CONTACT_HERO_FALLBACK;
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80">Support Team</p>
              <h1 className="mt-2 text-2xl font-black text-white">We are here to help</h1>
            </div>
          </div>

          <div className="sc-soft-card p-5">
            <ContactItem
              icon={<FiPhoneCall size={17} />}
              title="Call Us"
              details={['+880 1611-112222', 'Daily 9:00 AM - 11:00 PM']}
            />
            <div className="my-5 border-t border-slate-100" />
            <ContactItem
              icon={<FiMail size={17} />}
              title="Email Us"
              details={['support@shopcircuit.com', 'sales@shopcircuit.com']}
            />
            <div className="my-5 border-t border-slate-100" />
            <ContactItem
              icon={<FiMapPin size={17} />}
              title="Visit Us"
              details={['111 Bijoy Sarani, Dhaka 1215', 'Bangladesh']}
            />
          </div>

          <div className="sc-soft-card p-5">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]">
              <FiClock className="text-[#DB4444]" />
              Response Time
            </div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <FiCheckCircle className="text-[#DB4444]" size={14} />
                Live chat: typically under 5 minutes
              </li>
              <li className="flex items-center gap-2">
                <FiCheckCircle className="text-[#DB4444]" size={14} />
                Email replies: within 24 hours
              </li>
              <li className="flex items-center gap-2">
                <FiCheckCircle className="text-[#DB4444]" size={14} />
                Order issues: priority escalation enabled
              </li>
            </ul>
          </div>
        </aside>

        <section className="lg:col-span-7" data-reveal>
          <div className="sc-soft-card p-6 sm:p-8">
            <div className="mb-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Get In Touch</p>
              <h2 className="text-3xl font-black text-[#1A1A1A]">Send us a message</h2>
              <p className="mt-2 text-sm text-slate-600">
                Share your question and our team will respond with a clear next step.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="sc-input"
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className="sc-input"
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="sc-input"
                  required
                />
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="sc-input"
                  required
                />
              </div>

              <textarea
                name="message"
                placeholder="Tell us what you need help with"
                value={formData.message}
                onChange={handleChange}
                rows={7}
                className="sc-input min-h-[170px] resize-y"
                required
              />

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="sc-btn-primary sc-hover-smooth inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiSend size={14} />
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </section>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2" data-reveal>
        <article className="sc-soft-card overflow-hidden">
          <img
            src={LOCATION_IMAGE}
            alt="ShopCircuit location"
            className="h-52 w-full object-cover"
            onError={(event) => {
              event.currentTarget.src = LOCATION_IMAGE_FALLBACK;
            }}
          />
          <div className="p-5">
            <h3 className="text-lg font-bold text-[#1A1A1A]">Dhaka Office</h3>
            <p className="mt-2 text-sm text-slate-600">
              Walk-ins are welcome for business meetings and verified pickup support.
            </p>
          </div>
        </article>
        <article className="sc-soft-card p-6">
          <div className="mb-4 inline-flex rounded-full bg-[#DB4444]/10 p-2 text-[#DB4444]">
            <FiHeadphones size={18} />
          </div>
          <h3 className="text-lg font-bold text-[#1A1A1A]">Need immediate support?</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            For urgent payment or delivery issues, call us directly and mention your order number for fast resolution.
          </p>
          <div className="mt-5">
            <a
              href="tel:+8801611112222"
              className="sc-hover-smooth inline-flex rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-[#DB4444] hover:text-[#DB4444]"
            >
              +880 1611-112222
            </a>
          </div>
        </article>
      </section>
    </div>
  );
};

const ContactItem = ({ icon, title, details }) => (
  <div>
    <div className="mb-2 flex items-center gap-2 text-sm font-bold text-[#1A1A1A]">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#DB4444]/10 text-[#DB4444]">
        {icon}
      </span>
      {title}
    </div>
    <div className="space-y-1 pl-10 text-sm text-slate-600">
      {details.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  </div>
);

export default Contact;
